import React from "react";
import { View, Text } from "react-native";
import { api } from "../utils/convex-api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { Button } from "../ui/button";
import CloseIcon from "@/assets/svgs/close-icon";
import PencilIcon from "@/assets/svgs/pencil-icon";
import { useRouter } from "expo-router";

function ChildCard({
  child,
  curriculums,
  yearGroups,
}: {
  child: typeof api.parent.onboarding.queries.getChildById._returnType;
  curriculums: typeof api.tutor.onboarding.queries.getAvailableCurriculums._returnType;
  yearGroups: typeof api.parent.onboarding.queries.getYearGroupsList._returnType;
}) {
  const router = useRouter();
  const { data: subjects = [] } = useQuery(
    convexQuery(api.parent.onboarding.queries.getSubjectsForYearGroup, {
      yearGroupId: child.yearGroupId,
    })
  );
  const { mutateAsync: deleteChild } = useMutation({
    mutationFn: useConvexMutation(api.parent.onboarding.mutations.deleteChild),
  });

  const { data: qualificationsForYearGroup } = useQuery({
    ...convexQuery(
      api.parent.onboarding.queries.getAvailableQualificationsForYearGroup,
      { yearGroupId: child.yearGroupId }
    ),
    enabled: !!child.yearGroupId,
  });

  const handleDelete = async () => {
    await deleteChild({ studentId: child._id });
  };

  const handleEdit = () => {
    router.navigate(
      `/(app)/onboarding/(parent)/add-child?child=${JSON.stringify({
        _id: child._id,
        firstName: child.firstName,
        email: child.email,
        school: child.school,
        curriculum: child.curriculumId,
        yearGroup: child.yearGroupId,
        subjects: child.subjects,
      })}`
    );
  };

  return (
    <View
      key={child._id}
      className="bg-white shadow-soft-1 rounded-xl px-5 py-6 gap-8"
    >
      <View className="flex-row gap-2">
        <View className="flex-1">
          <Text className="font-geologica-medium font-medium text-base text-gray-800 leading-[1.5]">
            {child.firstName}
          </Text>
          <View className="h-2" />
          <Text className="font-geologica-regular font-normal text-sm text-gray-800 leading-[1.5]">
            {curriculums.find((c) => c.value === child.curriculumId)?.label}
          </Text>
          <Text className="font-geologica-regular font-normal text-sm text-gray-800 leading-[1.5]">
            {yearGroups.find((c) => c.value === child.yearGroupId)?.label}
          </Text>
          {child.school && (
            <Text className="font-geologica-regular font-normal text-sm text-gray-800 leading-[1.5]">
              {child.school}
            </Text>
          )}
        </View>
        <View className="flex-1">
          <Text className="font-geologica-medium font-medium text-base text-gray-800 leading-[1.5]">
            Subjects
          </Text>
          <View className="h-2" />
          <Text className="font-geologica-regular font-normal text-sm text-gray-800 leading-[1.5]">
            {(child.subjects ?? [])
              .map(
                (s) =>
                  `${subjects.find((c) => c.value === s.subjectId)?.label} ${
                    s.qualificationId
                      ? `- ${qualificationsForYearGroup?.find((c) => c.value === s.qualificationId)?.label ?? ""}`
                      : ""
                  }`
              )
              .join("\n")}
          </Text>
        </View>
      </View>

      <View className="flex-row justify-end">
        <Button borderLess icon onPress={handleEdit} size="sm">
          <Button.Icon
            as={PencilIcon}
            className="size-4"
            intent="primary"
            pathClassName="fill-none stroke-teal-600"
          />
          <Button.Text size="sm" intent="primary">
            Edit
          </Button.Text>
        </Button>
        <Button borderLess icon onPress={handleDelete} size="sm">
          <Button.Icon className="size-4" as={CloseIcon} intent="primary" />
          <Button.Text size="sm" intent="primary">
            Remove
          </Button.Text>
        </Button>
      </View>
    </View>
  );
}

export default ChildCard;
